import { ArrowLeft, Columns2, ListChecks, Moon, Sun } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, type RouteObject } from 'react-router-dom';
import { MainMenu } from '@/components/layout/shell/MainMenu';
import { WorkspaceCanvas } from '@/components/layout/workspace/WorkspaceCanvas';
import { WorkspaceProvider } from '@/components/layout/workspace/WorkspaceProvider';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import { useRightPanel } from '@/components/layout/workspace/rightPanelContext';
import type { RightPanelConfig } from '@/components/layout/workspace/rightPanelContext';
import type { PathResolver } from '@/components/layout/workspace/types';
import { ObserverProvider } from '@/components/observer/ObserverProvider';
import {
  Badge,
  Button,
  IconButton,
  EmptyState,
  PropertyList,
  PropertyRow,
  ToastProvider,
} from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { DisclosureExamples } from './DisclosureExamples';
import { LiveStepsDemo } from './LiveStepsDemo';
import { CompactStepsDemo } from './CompactStepsDemo';

const HOME = '/design/progress';
const NAMES: Record<string, string> = {
  'check-a': 'Проверка А',
  'record-a12': 'Запись А-12',
  'record-b08': 'Запись Б-08',
};
const resolve: PathResolver = (path) => {
  const id = path.split('/').at(-1) ?? '';
  return {
    type: path === HOME || path === HOME + '/' || path === HOME + '/compact' ? 'view' : 'object',
    serviceId: 'progress-sample',
    title:
      path === HOME + '/compact'
        ? 'Длинная проверка'
        : path.includes('/example/')
          ? 'Второй пример'
          : ((Object.hasOwn(NAMES, id) ? NAMES[id] : undefined) ?? 'Раскрытия и шаги'),
    icon: ListChecks,
    homeView: HOME,
  };
};
export function ProgressPatterns() {
  return (
    <ToastProvider>
      <WorkspaceProvider homePath={HOME} resolve={resolve} storageKey="kaname.progress.workspace.v1">
        <ObserverProvider home="core" storageKey="kaname.progress.observer.v1">
          <SampleShell />
        </ObserverProvider>
      </WorkspaceProvider>
    </ToastProvider>
  );
}
function SampleShell() {
  const ws = useWorkspace();
  const { mode, toggle } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const routes = useMemo<RouteObject[]>(
    () => [
      { path: '/example/:id', element: <Examples /> },
      { path: '/compact', element: <CompactExample /> },
      { path: '/object/:id', element: <ObjectPage /> },
      { path: '*', element: <Examples /> },
    ],
    [],
  );
  const config = useMemo<RightPanelConfig>(
    () => ({
      storageKey: 'kaname.progress.panels.v1',
      render: (item) => <ObjectContents id={item.id} />,
    }),
    [],
  );
  return (
    <div className="flex h-screen flex-col bg-frame text-fg">
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line bg-canvas px-6 py-3">
        <Link
          to="/design#progress-patterns"
          className="inline-flex items-center gap-2 text-xs font-medium text-fg-muted hover:text-brand-fg"
        >
          <ArrowLeft className="size-4" />
          Галерея
        </Link>
        <div className="order-first w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">
          <h1 className="type-subheading">Раскрытия и шаги</h1>
          <p className="text-xs text-fg-muted">Общие элементы · вымышленные данные</p>
        </div>
        <Badge>Образец</Badge>
        <Button variant="secondary" size="sm" onClick={() => ws.openBeside(HOME + '/example/second')}>
          <Columns2 className="size-4" />
          Второе окно
        </Button>
        <IconButton
          variant="secondary"
          aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
          onClick={toggle}
        >
          {mode === 'dark' ? <Sun /> : <Moon />}
        </IconButton>
      </header>
      <div data-shell="" className="relative flex min-h-0 flex-1 gap-3 p-3">
        <MainMenu
          expanded={expanded}
          onToggleExpand={() => setExpanded((value) => !value)}
          homeActive
          onHome={() => ws.go(HOME)}
          activeId={null}
          sections={[]}
          tools={[]}
          onSelect={() => ws.go(HOME)}
        />
        <WorkspaceCanvas routes={routes} rightPanel={config} />
      </div>
    </div>
  );
}
function Examples() {
  const { id } = useParams();
  const ws = useWorkspace();
  const panel = useRightPanel();
  const onEntityOpen = (entityId: string) =>
    panel.open({
      id: entityId,
      path: HOME + '/object/' + entityId,
      title: NAMES[entityId],
      eyebrow: 'Объект',
    });
  return (
    <main className="flex min-w-0 flex-col gap-6 p-6">
      <header>
        <p className="mb-2 type-overline text-fg-subtle">Образцы · волна 3</p>
        <h2 data-panel-default-focus="" tabIndex={-1} className="type-title focus-visible:outline-none">
          {id ? 'Второй пример' : 'Раскрытия и шаги'}
        </h2>
        <p className="mt-2 text-sm text-fg-muted">
          Раскрытые разделы сохраняются для каждой вкладки. Ход проверки — пробные данные.
        </p>
      </header>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={() => ws.go(HOME + '/example/second')}>
          Открыть второй пример
        </Button>
        <Button size="sm" variant="secondary" onClick={() => ws.go(HOME)}>
          Основной пример
        </Button>
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-8 xl:grid-cols-2">
        <DisclosureExamples scope="sample" onEntityOpen={onEntityOpen} />
        <section className="min-w-0">
          <h3 className="mb-3 type-subheading">Живой список шагов</h3>
          <LiveStepsDemo scope="sample" onEntityOpen={onEntityOpen} />
        </section>
      </div>
    </main>
  );
}
function ObjectContents({ id }: { id: string }) {
  if (!Object.hasOwn(NAMES, id))
    return (
      <EmptyState title="Запись не найдена" description="Такой записи нет в вымышленном наборе данных." />
    );
  return (
    <PropertyList>
      <PropertyRow label="Название">{Object.hasOwn(NAMES, id) ? NAMES[id] : 'Запись не найдена'}</PropertyRow>
      <PropertyRow label="Данные">Вымышленные, только для образца</PropertyRow>
      <PropertyRow label="Состояние">Доступна</PropertyRow>
    </PropertyList>
  );
}
function ObjectPage() {
  const { id = '' } = useParams();
  return (
    <article className="p-6">
      <h2 data-panel-default-focus="" tabIndex={-1} className="mb-6 type-title">
        {Object.hasOwn(NAMES, id) ? NAMES[id] : 'Запись не найдена'}
      </h2>
      <ObjectContents id={id} />
    </article>
  );
}

function CompactExample() {
  const panel = useRightPanel();
  return (
    <main className="flex min-w-0 flex-col gap-6 p-6">
      <header>
        <p className="mb-2 type-overline text-fg-subtle">Образцы · волна 3</p>
        <h2 data-panel-default-focus="" tabIndex={-1} className="type-title">
          Длинная проверка
        </h2>
      </header>
      <CompactStepsDemo
        scope="sample"
        onEntityOpen={(id) =>
          panel.open({ id, path: HOME + '/object/' + id, title: NAMES[id], eyebrow: 'Объект' })
        }
      />
    </main>
  );
}
